/**
 * Link ONE job to a company by hand (epic 2026-09-25-chopbook-company-lists,
 * 00 D3 / Q4): for a job with no LinkedIn company id, pick an existing company
 * record or create one from the job's name. Only this job changes; other
 * people's jobs with the same text do not.
 *
 * Opened from the person panel's Experience row. Native `<dialog>` +
 * `showModal()`, like `CompanyListDialog`.
 */
import { Avatar, Spinner } from "@nu-assist/ui";
import { skipToken, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, Plus, Unlink, X } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";

import type { CompanyListsClient } from "../../clients/companyLists.client";
import { JOB_COMPANIES_KEY, jobCompaniesClient } from "./useJobCompanies";

interface LinkCompanyDialogProps {
  positionId: string;
  /** The company as the job names it: the search starts there, and "create" uses it. */
  jobCompanyName: string | null;
  /** The company the job is linked to by hand now, if any (offers "Unlink"). */
  linkedTo?: { company_id: string; name: string | null } | null;
  /** Refetch the person after a change (their Experience rows). */
  personQueryKey: readonly unknown[];
  onClose: () => void;
  client?: CompanyListsClient;
}

const DEBOUNCE_MS = 250;

function useDebounced(value: string, ms: number): string {
  const [out, setOut] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setOut(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return out;
}

export function LinkCompanyDialog({
  positionId,
  jobCompanyName,
  linkedTo,
  personQueryKey,
  onClose,
  client = jobCompaniesClient,
}: LinkCompanyDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const inputId = useId();
  const queryClient = useQueryClient();
  const [q, setQ] = useState(jobCompanyName ?? "");
  const term = useDebounced(q.trim(), DEBOUNCE_MS);

  const results = useQuery({
    queryKey: ["company-search", term],
    queryFn: term.length >= 2 ? () => client.searchCompanies(term) : skipToken,
    staleTime: 30 * 1000,
  });

  const done = () => {
    void queryClient.invalidateQueries({ queryKey: personQueryKey });
    void queryClient.invalidateQueries({ queryKey: JOB_COMPANIES_KEY });
    onClose();
  };
  const link = useMutation({
    mutationFn: (target: { company_id: string } | { new_company_name: string }) =>
      client.linkPosition(positionId, target),
    onSuccess: done,
  });
  const unlink = useMutation({
    mutationFn: () => client.unlinkPosition(positionId),
    onSuccess: done,
  });
  const busy = link.isPending || unlink.isPending;
  const error = link.error ?? unlink.error;

  const busyRef = useRef(busy);
  busyRef.current = busy;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    // jsdom has no showModal(); the attribute is the same dialog, non-modal.
    if (!el.open) {
      if (typeof el.showModal === "function") el.showModal();
      else el.setAttribute("open", "");
    }
    const onCancel = (e: Event) => {
      e.preventDefault();
      if (!busyRef.current) onCloseRef.current();
    };
    el.addEventListener("cancel", onCancel);
    return () => el.removeEventListener("cancel", onCancel);
  }, []);

  const typed = q.trim();
  const exact = (results.data ?? []).some(
    (r) => (r.name ?? "").toLowerCase() === typed.toLowerCase()
  );

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby={`${inputId}-title`}
      data-testid="link-company-dialog"
      className="fixed inset-0 z-50 m-0 flex w-full max-w-none items-center justify-center border-none bg-transparent p-4 backdrop:bg-black/50"
    >
      <div className="flex w-full max-w-lg flex-col overflow-hidden rounded-lg border border-border bg-surface shadow-lg">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 id={`${inputId}-title`} className="text-h3 font-semibold text-text">
            Link this job to a company
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
            className="rounded-md p-1 text-text-muted hover:bg-surface-raised disabled:opacity-40"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        <div className="flex flex-col gap-3 px-6 py-5">
          <p className="text-body text-text-secondary">
            Only this job changes. Its company then shows here, and this person appears under that
            company's "People we know".
          </p>
          <label htmlFor={inputId} className="flex flex-col gap-1">
            <span className="text-label font-medium text-text">Company</span>
            <input
              id={inputId}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              disabled={busy}
              placeholder="Search companies by name"
              data-testid="link-company-search"
              className="rounded-md border border-border bg-bg px-3 py-2 text-body text-text focus:border-accent focus:outline-none"
            />
          </label>
          <ul className="flex max-h-72 flex-col overflow-y-auto" data-testid="link-company-results">
            {results.isFetching && (
              <li className="px-2 py-2">
                <Spinner size="sm" label="Searching…" />
              </li>
            )}
            {(results.data ?? []).map((r) => (
              <li key={r.company_id}>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => link.mutate({ company_id: r.company_id })}
                  data-testid="link-company-result"
                  className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-left hover:bg-surface-raised disabled:opacity-40"
                >
                  <Avatar name={r.name ?? "?"} shape="square" size="sm" />
                  <span className="min-w-0">
                    <span className="block truncate text-body text-text">{r.name}</span>
                    {r.url && (
                      <span className="block truncate text-caption text-text-muted">{r.url}</span>
                    )}
                  </span>
                </button>
              </li>
            ))}
            {typed.length >= 2 && !exact && (
              <li>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => link.mutate({ new_company_name: typed })}
                  data-testid="link-company-create"
                  className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-left text-body text-accent-ink hover:bg-surface-raised disabled:opacity-40"
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  Create “{typed}”
                </button>
              </li>
            )}
          </ul>
          {error && (
            <p className="flex items-center gap-2 text-caption text-error" role="alert">
              <AlertCircle className="h-4 w-4" aria-hidden="true" />
              {error instanceof Error ? error.message : "Could not link the job."}
            </p>
          )}
        </div>
        {linkedTo && (
          <div className="flex items-center justify-between border-t border-border px-6 py-3">
            <span className="truncate text-caption text-text-muted">
              Linked to {linkedTo.name ?? "a company"}
            </span>
            <button
              type="button"
              onClick={() => unlink.mutate()}
              disabled={busy}
              data-testid="link-company-unlink"
              className="flex items-center gap-1 rounded-md px-2 py-1 text-caption text-text-secondary hover:bg-surface-raised disabled:opacity-40"
            >
              <Unlink className="h-4 w-4" aria-hidden="true" />
              Unlink
            </button>
          </div>
        )}
      </div>
    </dialog>
  );
}
